import { useEffect, useMemo, useState, useCallback } from 'react';
import { listBatches, archiveBatch, deleteBatch } from '../api/batchesApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';

const DEFAULT_PAGE_SIZE = 10;

export function useBatchesListScreen() {
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'archived' | 'all'
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 1,
  });

  const queryParams = useMemo(() => {
    return {
      page,
      pageSize,
      viewMode,
      searchText: searchText.trim(),
    };
  }, [page, pageSize, viewMode, searchText]);

  const loadBatches = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await listBatches(queryParams);
      setItems(response.items || []);
      setMeta({
        totalCount: response.total_count ?? 0,
        page: response.page ?? 1,
        pageSize: response.page_size ?? pageSize,
        totalPages: response.total_pages ?? 1,
      });
    } catch (requestError) {
      const msg = requestError?.response?.data?.detail || 'Failed to load batches.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setIsLoading(false);
    }
  }, [queryParams, pageSize]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const handleArchive = async (id) => {
    try {
      const res = await archiveBatch(id);
      appToast.success(res?.message || 'Batch status updated successfully.');
      loadBatches();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to update batch status.';
      appToast.error(typeof msg === 'string' ? msg : 'Failed to update batch status.');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await appConfirm({
      title: 'Delete Batch',
      message: 'Are you sure you want to delete this batch cohort? This action can be audited.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        await deleteBatch(id);
        appToast.success('Batch deleted successfully.');
        loadBatches();
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Failed to delete batch.';
        appToast.error(typeof msg === 'string' ? msg : 'Failed to delete batch.');
      }
    }
  };

  const resetFilters = () => {
    setSearchText('');
    setPage(1);
  };

  const actions = {
    setViewMode: (mode) => {
      setViewMode(mode);
      setPage(1);
    },
    setSearchText: (text) => {
      setSearchText(text);
      setPage(1);
    },
    setPage,
    setPageSize,
    resetFilters,
    handleArchive,
    handleDelete,
    refresh: loadBatches,
  };

  return {
    state: {
      viewMode,
      searchText,
      page,
      pageSize,
      isLoading,
      error,
      rows: items,
      totalCount: meta.totalCount,
      totalPages: meta.totalPages,
    },
    actions,
  };
}
